/**
 * The shortcut row under the ask box, shaped by what actually broke.
 *
 * The old list was six fixed strings — the same on an account with a dead
 * channel as on one that has never sent a message. Here the account's most
 * recent *send* failures come first, translated by category, and the generic
 * set only fills whatever room is left.
 *
 * Where the data comes from matters more than the mapping: the categories are
 * the ones `normalizeDeliveryError` already produces server-side, read from
 * `GET /api/internal/support/send-errors`. Nothing here polls — the home asks
 * once on mount and revalidates when the socket reports a new failure.
 *
 * Icons are per category on purpose. The previous glyphs were abstract shapes
 * with a colour, which carried no information at all: a square meant nothing
 * about "the 24h window expired".
 */

/** One entry per category `normalizeDeliveryError` can emit. */
const BY_CATEGORY = {
  window: {
    icon: "clock",
    tone: "warning",
    text: "Janela de 24h expirou",
  },
  template: {
    icon: "document",
    tone: "info",
    text: "Problema com o template",
  },
  rate_limit: {
    icon: "gauge",
    tone: "warning",
    text: "Limite de envio atingido",
  },
  recipient: {
    icon: "user",
    tone: "danger",
    text: "Número não recebeu",
  },
  media: {
    icon: "image",
    tone: "pink",
    text: "Falha ao enviar mídia",
  },
  account: {
    icon: "shield",
    tone: "danger",
    text: "Restrição na conta da Meta",
  },
  registration: {
    icon: "phone",
    tone: "danger",
    text: "Número não registrado",
  },
  marketing: {
    icon: "megaphone",
    tone: "info",
    text: "Mensagem de marketing bloqueada",
  },
  temporary: { icon: "retry", tone: "sky", text: "Falha temporária no envio" },
  timeout: { icon: "retry", tone: "sky", text: "Envio sem confirmação" },
  meta: { icon: "alert", tone: "danger", text: "Erro reportado pela Meta" },
};

/** Shown when the account has no recent send failure to talk about. */
export const FALLBACK_SUGGESTIONS = Object.freeze([
  { text: "Meu número desconectou", icon: "phone", tone: "warning" },
  { text: "Segunda via da fatura", icon: "document", tone: "success" },
  { text: "Permissões de usuário", icon: "user", tone: "info" },
  { text: "Delay no envio", icon: "clock", tone: "sky" },
  { text: "Conectar integração", icon: "plug", tone: "pink" },
]);

const MAX_SUGGESTIONS = 6;

/**
 * @param {{ sendErrors?: Array<{category: string, code: string|null}> }} input
 * @returns {Array<{text: string, icon: string, tone: string}>}
 */
export function deriveSuggestions({ sendErrors = [] } = {}) {
  const seen = new Set();
  const derived = [];

  for (const error of sendErrors) {
    const entry = BY_CATEGORY[error?.category];
    if (!entry || seen.has(error.category)) continue;
    seen.add(error.category);
    derived.push({
      ...entry,
      // The Meta code is what the person sees in the inbox, so it is the
      // fastest way for them to recognise their own problem in this row.
      text: error.code ? `${entry.text} (${error.code})` : entry.text,
    });
  }

  const remaining = MAX_SUGGESTIONS - derived.length;
  if (remaining <= 0) return derived.slice(0, MAX_SUGGESTIONS);

  return [...derived, ...FALLBACK_SUGGESTIONS.slice(0, remaining)];
}

export default deriveSuggestions;
